import * as React from 'react'
import {ReactElement} from 'react'
import {Nullable} from '../../../../app/TypeHelpers'
import NatMenuMasterRepo from '../repository/NatMenuMasterRepo'
import styled from 'styled-components'
import {Colors, FontWeights} from '../../../../styledComponents/Constants'
import {MenuData, NatGetMenuMasterAuthedAdmin} from '../repository/NatGetMenuMasterAuthedAdmin'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import {
    AsyncCommunicationProps,
    withAsyncCommunication,
} from '../../../../components/shared/hoc/withAsyncCommunication'
import EventEmitterExAdmin from '../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {Accordion, AccordionDetails, AccordionSummary} from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import {ADMIN_HEADER_HEIGHT} from '../../../../components/header/AdminHeaderAreaParts'
import {NavigateFunction} from 'react-router-dom'

interface Props {
    navigate: NavigateFunction
    menuMasterRepo: NatMenuMasterRepo,
    close: () => void,
    opened: boolean,
}

interface States {
    expandedMenuIndex: number | false
    menuDataList: Nullable<MenuData[]>
}

interface ExternalProps {
    eventEmitter: EventEmitterExAdmin
}

const PARENT_MENU_HEIGHT = 56

class NatAdminMenuMasterComponentSp
    extends React.Component<Props & AsyncCommunicationProps & ExternalProps, States> {
    constructor(props: Props & AsyncCommunicationProps & ExternalProps) {
        super(props)
        this.state = {
            expandedMenuIndex: false,
            menuDataList: null,
        }
    }

    async componentDidMount(): Promise<void> {
        const menuMasterAdmin: Nullable<NatGetMenuMasterAuthedAdmin> = await this.props.asyncCallFunction(
            this.props.menuMasterRepo.getMenuMasterAuthedAdmin.bind(this.props.menuMasterRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            },
        )
        if (menuMasterAdmin && menuMasterAdmin.details) {
            this.setState({menuDataList: menuMasterAdmin.details})
        }
    }

    componentDidUpdate(prevProps: Props): void {
        if (prevProps.opened && !this.props.opened) {
            this.setState({expandedMenuIndex: false})
        }
    }

    handleChangeAccordion = (menuIndex: number) => () => {
        if (this.state.expandedMenuIndex === menuIndex) {
            this.setState({expandedMenuIndex: false})
        } else {
            this.setState({expandedMenuIndex: menuIndex})

            const parentMenuTop = ADMIN_HEADER_HEIGHT +
                (PARENT_MENU_HEIGHT + 1) * menuIndex  // +1 は borderの太さの分
            window.scrollTo(0, parentMenuTop - 28)
        }
    }

    handleClickChildMenu = (url: string) => () => {
        this.props.close()
        this.setState({expandedMenuIndex: false})
        this.props.navigate(url)
    }

    // TODO スマホ対応画面が増えた時に見直す
    isPcSite(subMenuTitle: string): boolean {
        return subMenuTitle !== '申請状況一覧'
    }

    render(): Nullable<ReactElement> {
        if (!this.state.menuDataList) {
            return null
        }
        return (<>
            {this.state.menuDataList.map((menuMaster, index) => (
                <MenuAccordion
                    expanded={this.state.expandedMenuIndex === index}
                    onChange={this.handleChangeAccordion(index)}
                    key={index}
                    square={true}
                >
                    <MenuAccordionSummary
                        expandIcon={<ExpandMoreOutlinedIcon/>}
                        aria-controls={`panel${index}a-content`}
                        id={`panel${index}a-header`}
                    >
                        <ParentMenu>
                            {menuMaster.menuName}
                        </ParentMenu>
                    </MenuAccordionSummary>
                    <MenuAccordionDetails>
                        {menuMaster.childMenuInfo && menuMaster.childMenuInfo
                            .map((childMenu, childIndex) => (
                                <ChildMenu
                                    key={childIndex}
                                    onClick={this.handleClickChildMenu(childMenu.url)}
                                >
                                    {childMenu.menuName}
                                    {this.isPcSite(childMenu.menuName) &&
                                    <PcSiteTag/>
                                    }
                                </ChildMenu>
                            ))
                        }
                    </MenuAccordionDetails>
                </MenuAccordion>
            ))}
        </>)
    }
}

export default withAsyncCommunication(NatAdminMenuMasterComponentSp)

const MenuAccordion = withStyles({
    root: {
        width: '100%',
        '&:not(:last-child)': {
            borderBottom: '1px solid rgba(217, 217, 217, 0.5)',
        },
        boxShadow: 'none',
        lineHeight: 1.5,
        '&:before': {
            display: 'none',
        },
        '&$expanded': {
            margin: 'auto',
        },
    },
    expanded: {},
})(Accordion)

const MenuAccordionSummary = withStyles({
    root: {
        padding: '0 16px',
        minHeight: PARENT_MENU_HEIGHT,
        '&$expanded': {
            minHeight: PARENT_MENU_HEIGHT,
        },
        overflow: 'hidden',
    },
    content: {
        '&$expanded': {
            margin: '12px 0',
        },
    },
    expandIcon: {
        color: Colors.AccentDark,
    },
    expanded: {},
})(AccordionSummary)

const ParentMenu = styled.div`
  color: ${Colors.Body};
  font-size: 14px;
  font-weight: ${FontWeights.Bold};
`

const MenuAccordionDetails = withStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
    },
})(AccordionDetails)

const ChildMenu = styled.div`
  padding: 16px 0 16px 32px;
  color: ${Colors.Body};
  font-size: 14px;

  :hover {
    background-color: #e2e5f4;
  }
`

const PcSiteTag = styled.div`
  margin-left: 8px;
  display: inline-block;
  width: 60px;
  line-height: 18px;
  border-radius: 9px;
  text-align: center;
  font-size: 12px;
  font-weight: ${FontWeights.Normal};
  color: ${Colors.AccentDark};
  background: ${Colors.Background};

  &::before {
    content: 'PCサイト';
  }
`
